import { useState } from 'react';
import { Building2, PackagePlus, Store, Swords } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useRunSeoCompetitors,
  useSeoCompetitorsQuery,
  type ApiSeoCompetitorSnapshot,
} from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  RangeTabs,
  SITE_COLOR,
  SITE_LABEL,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  SeoTrend,
  TD,
  TH,
  TR,
  fmtNumber,
} from './components/seo-ui';

const RANGES = [
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' },
];

const VERTICAL_LABEL: Record<string, string> = {
  market: 'Market (goods)',
  vehicles: 'Vehicles',
  property: 'Property',
  services: 'Services',
  materials: 'Building materials',
  unclassified: 'Unclassified',
};

function TopList({ rows }: { rows: { key: string; live: number }[] | undefined }) {
  if (!rows?.length) return <p className="py-3 text-sm text-gray-600">No reading yet.</p>;
  const peak = Math.max(1, ...rows.map((r) => r.live));
  return (
    <ul className="space-y-1.5">
      {rows.slice(0, 12).map((r) => (
        <li key={r.key} className="flex items-center gap-3 text-sm">
          <span className="w-44 shrink-0 truncate text-gray-900" title={r.key}>
            {r.key}
          </span>
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
            <span className="block h-full rounded-full bg-gray-400" style={{ width: `${(r.live / peak) * 100}%` }} />
          </span>
          <span className="w-14 shrink-0 text-right tabular-nums text-gray-900">{fmtNumber(r.live)}</span>
        </li>
      ))}
    </ul>
  );
}

function VerticalTable({ latest }: { latest: ApiSeoCompetitorSnapshot }) {
  const by = Object.fromEntries(latest.sources.map((s) => [s.source, s]));
  const verticals = Array.from(
    new Set(['jiji', 'meqasa'].flatMap((name) => Object.keys(by[name]?.verticals ?? {}))),
  );
  // Our verticals against the ff-prospect vertical that sells the same thing.
  const ours: Record<string, number | undefined> = { market: latest.ours.market, property: latest.ours.homes };
  const num = cn(TD, 'text-right tabular-nums');
  return (
    <table className="w-full text-sm">
      <thead>
        <tr>
          <th className={TH}>Vertical</th>
          <th className={cn(TH, 'text-right')}>FeelyFeely</th>
          <th className={cn(TH, 'text-right')}>Jiji</th>
          <th className={cn(TH, 'text-right')}>Jiji new, 7 days</th>
          <th className={cn(TH, 'text-right')}>Meqasa</th>
          <th className={cn(TH, 'text-right')}>Meqasa new, 7 days</th>
        </tr>
      </thead>
      <tbody>
        {verticals.map((v) => (
          <tr key={v} className={TR}>
            <td className={cn(TD, 'text-gray-900')}>{VERTICAL_LABEL[v] ?? v}</td>
            <td className={cn(num, 'font-semibold text-gray-900')}>{fmtNumber(ours[v])}</td>
            <td className={num}>{fmtNumber(by.jiji?.verticals[v]?.live)}</td>
            <td className={cn(num, 'text-gray-600')}>{fmtNumber(by.jiji?.verticals[v]?.new_7d)}</td>
            <td className={num}>{fmtNumber(by.meqasa?.verticals[v]?.live)}</td>
            <td className={cn(num, 'text-gray-600')}>{fmtNumber(by.meqasa?.verticals[v]?.new_7d)}</td>
          </tr>
        ))}
        <tr className={TR}>
          <td className={cn(TD, 'text-gray-900')}>Local businesses</td>
          <td className={cn(num, 'font-semibold text-gray-900')}>{fmtNumber(latest.ours.locals)}</td>
          <td className={cn(TD, 'text-right text-gray-400')} colSpan={4}>
            Neither lists these
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * `/admin/seo/competitors` — how much each competing marketplace lists, against
 * what we list, read once a night from ff-prospect's crawl
 * (ff-api service/seo_competitors.py).
 */
export default function SeoCompetitorsPage() {
  const [days, setDays] = useState<number>(90);
  const { data, isLoading, error } = useSeoCompetitorsQuery(days);
  const runNow = useRunSeoCompetitors();
  const latest = data?.latest;
  const history = data?.history ?? [];
  const by = Object.fromEntries((latest?.sources ?? []).map((s) => [s.source, s]));
  const series = (name: string) =>
    history.map((h) => ({
      day: h.day,
      value: name === 'feelyfeely' ? h.ours : ((h[name] as { live: number } | undefined)?.live ?? 0),
    }));

  return (
    <MarketingPageShell
      title="Competitors"
      description="What Jiji and Meqasa list in Ghana against what we list, read every night."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'Competitors' }]}
      icon={Swords}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <RangeTabs value={days} options={RANGES} onChange={setDays} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => runNow.mutate({})}
            disabled={runNow.isPending || runNow.isSuccess}
          >
            {runNow.isSuccess ? 'Queued: results in about 2 minutes' : 'Take a reading now'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <SeoError>Could not load competitor readings.</SeoError>}
        {runNow.isError && <SeoError>Could not start the reading.</SeoError>}

        {!isLoading && !latest ? (
          <EmptyState label="No reading has been taken yet. The first one runs overnight, or take one now." />
        ) : (
          <>
            <SeoKpiRow>
              <SeoKpi label="FeelyFeely listings" value={latest?.ours.total} icon={Store} hint="Published, Ghana" loading={isLoading} />
              <SeoKpi
                label="Jiji listings"
                value={by.jiji?.live}
                icon={Building2}
                hint={latest ? `Seen in the last ${latest.live_days} days` : undefined}
                loading={isLoading}
              />
              <SeoKpi
                label="Meqasa listings"
                value={by.meqasa?.live}
                icon={Building2}
                hint={latest ? `Seen in the last ${latest.live_days} days` : undefined}
                loading={isLoading}
              />
              <SeoKpi
                label="New on Jiji, 7 days"
                value={by.jiji?.new_7d}
                icon={PackagePlus}
                hint={`Meqasa: ${fmtNumber(by.meqasa?.new_7d)}`}
                loading={isLoading}
              />
            </SeoKpiRow>

            <SeoPanel
              title="Listings over time"
              subtitle={`${history.length} nightly reading${history.length === 1 ? '' : 's'}${latest ? `, latest ${latest.day}` : ''}. Each chart has its own scale: the marketplaces differ too much in size to share one.`}
            >
              <div className="grid gap-4 md:grid-cols-3">
                {(['feelyfeely', ...(data?.competitors ?? [])] as string[]).map((name) => (
                  <div key={name}>
                    <div className="mb-1 flex items-center gap-2 text-sm font-medium text-gray-900">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: SITE_COLOR[name] }} />
                      {SITE_LABEL[name] ?? name}
                    </div>
                    <SeoTrend
                      small
                      height={140}
                      data={series(name)}
                      series={[{ key: 'value', name: SITE_LABEL[name] ?? name, color: SITE_COLOR[name] ?? '#64748B' }]}
                    />
                  </div>
                ))}
              </div>
            </SeoPanel>

            {latest && (
              <SeoPanel title="Listings by vertical" subtitle="Ours against theirs, and how fast they add supply" flush>
                <VerticalTable latest={latest} />
              </SeoPanel>
            )}

            <div className="grid gap-4 lg:grid-cols-3">
              <SeoPanel title="Jiji: largest categories" subtitle="Where their supply is">
                <TopList rows={latest?.categories.jiji} />
              </SeoPanel>
              <SeoPanel title="Jiji: largest areas" subtitle="By listings">
                <TopList rows={latest?.regions.jiji} />
              </SeoPanel>
              <SeoPanel title="Meqasa: largest areas" subtitle="By listings">
                <TopList rows={latest?.regions.meqasa} />
              </SeoPanel>
            </div>

            {latest && (
              <SeoNote>
                Competitor numbers count listings our crawler saw in the last {latest.live_days} days. The crawler
                revisits part of a large site each day, so Jiji's figure is a floor on what is live there, not a full
                count. Our own figure is every published listing.
              </SeoNote>
            )}
          </>
        )}
      </div>
    </MarketingPageShell>
  );
}
